/**
 * The coordinate vocabulary of "Ausrichtung korrigieren".
 *
 * Everything here is pure, because this is the part of the screen that is
 * easy to get subtly wrong and impossible to see going wrong: a quadrilateral
 * that is one swapped pair away from correct still looks like a quadrilateral
 * on screen, and only comes back as a mirrored page half a minute later.
 *
 * The contract with the worker (`matching.homography_from_corners`) is:
 *
 *  - four points, **fractions** of the scan's own width and height, never
 *    pixels — the worker multiplies them by the scan's real size, which is
 *    not the size the browser drew;
 *  - in the fixed order top-left, top-right, bottom-right, bottom-left. The
 *    order is *positional*, not geometric: index 0 is mapped onto the
 *    template's top-left corner whatever the person dragged it onto. So a
 *    drag never reorders the array — dragging handle 0 past handle 1 makes an
 *    invalid quadrilateral, which `quadProblem` refuses, rather than a
 *    silently mirrored page.
 */

export interface Corner {
  /** Fraction of the scan's width, 0 at the left edge. */
  x: number
  /** Fraction of the scan's height, 0 at the top edge. */
  y: number
}

export type Quad = readonly [Corner, Corner, Corner, Corner]

/** Index → what the handle means. The order is the worker's order. */
export const CORNER_LABELS = ['oben links', 'oben rechts', 'unten rechts', 'unten links'] as const

/**
 * Where the handles start.
 *
 * Just inside the image, so all four are visible and grabbable even on a scan
 * that fills the frame edge to edge. This is a starting position, not a
 * proposal: nothing measured the page, and the screen must not imply that
 * anything did.
 */
export const INITIAL_INSET = 0.08

export function initialQuad(): Quad {
  const a = INITIAL_INSET
  const b = 1 - INITIAL_INSET
  return [
    { x: a, y: a },
    { x: b, y: a },
    { x: b, y: b },
    { x: a, y: b },
  ]
}

export function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0
  return Math.min(1, Math.max(0, value))
}

/** Replaces one corner, clamped into the image. Never reorders. */
export function moveCorner(quad: Quad, index: number, next: Corner): Quad {
  const moved = quad.map((corner, i) =>
    i === index ? { x: clamp01(next.x), y: clamp01(next.y) } : corner,
  )
  return moved as unknown as Quad
}

/** Keyboard nudge: a relative step in fractions, clamped like a drag. */
export function nudgeCorner(quad: Quad, index: number, dx: number, dy: number): Quad {
  const current = quad[index]
  return moveCorner(quad, index, { x: current.x + dx, y: current.y + dy })
}

/**
 * Where a pointer sits, in image fractions.
 *
 * Absolute rather than accumulated from deltas: a drag built out of deltas
 * drifts away from the cursor as soon as one move is clamped at an edge, and
 * on this screen the interesting corners are exactly the ones at the edge.
 * `grab` is the offset between the pointer and the handle's centre at
 * pointer-down, so the handle does not jump under the cursor.
 *
 * The rect must be the box the image is *drawn* in, laid out at the image's
 * own aspect ratio. With `contain` there would be letterboxing inside that
 * box and this function would be wrong by the size of the bars.
 */
export function cornerFromPointer(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
  grab: { x: number; y: number } = { x: 0, y: 0 },
): Corner {
  const width = rect.width || 1
  const height = rect.height || 1
  return {
    x: clamp01((clientX - rect.left) / width - grab.x),
    y: clamp01((clientY - rect.top) / height - grab.y),
  }
}

/** The wire shape: `[[x, y], …]` in the worker's order, fractions untouched. */
export function toApiCorners(quad: Quad): number[][] {
  return quad.map((corner) => [corner.x, corner.y])
}

/** Twice the signed area (shoelace). Positive for our order — see below. */
export function signedArea2(quad: Quad): number {
  let sum = 0
  for (let i = 0; i < quad.length; i++) {
    const a = quad[i]
    const b = quad[(i + 1) % quad.length]
    sum += a.x * b.y - b.x * a.y
  }
  return sum
}

/**
 * The smallest quadrilateral worth analysing, as a share of the scan.
 *
 * Not a taste threshold: a sliver of a page warps to a template-sized image
 * from almost no pixels, and every field on it is noise. 2 % is well below
 * any real form on any real photograph — a form filling a fifth of the frame
 * is already 20 %.
 */
export const QUAD_MIN_AREA = 0.02

export type QuadProblem = { code: 'crossed' | 'tiny'; message: string }

/**
 * Why this quadrilateral cannot be used, or null.
 *
 * Validated here because **neither side validates it**: the gateway checks
 * only that there are four points in [0, 1], and the worker happily builds a
 * transform from a bow-tie and produces a folded, unusable page several
 * minutes later. A refusal the person can see and fix is worth more than a
 * job that fails.
 *
 * The test is strict convexity with the right orientation. Every perspective
 * image of a rectangle is a convex quadrilateral, and in image coordinates
 * (y downwards) the order TL → TR → BR → BL turns clockwise, which makes
 * every cross product positive. One non-positive turn means two handles have
 * been dragged past each other — the page would come back mirrored or folded.
 */
export function quadProblem(quad: Quad): QuadProblem | null {
  for (let i = 0; i < quad.length; i++) {
    const a = quad[i]
    const b = quad[(i + 1) % quad.length]
    const c = quad[(i + 2) % quad.length]
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x)
    if (cross <= 0) {
      return {
        code: 'crossed',
        message:
          'Die vier Punkte bilden kein Viereck. Ziehen Sie sie reihum auf die Ecken des Formulars: oben links, oben rechts, unten rechts, unten links.',
      }
    }
  }
  if (signedArea2(quad) / 2 < QUAD_MIN_AREA) {
    return {
      code: 'tiny',
      message:
        'Der markierte Bereich ist zu klein. Ziehen Sie die Punkte auf die Ecken des ganzen Formulars.',
    }
  }
  return null
}

/** "12 % von links, 8 % von oben" — the position in words, for the handle's
 *  accessible name and the live region. */
export function cornerPositionText(corner: Corner): string {
  const percent = (value: number) => `${Math.round(value * 100)} %`
  return `${percent(corner.x)} von links, ${percent(corner.y)} von oben`
}
